import { PAGES, UserDetailsPayload } from "@dune/types";
import { Avatar, AvatarFallback, AvatarImage } from "@dune/ui";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@dune/ui";
import { SidebarInset, SidebarProvider, useSidebar } from "@dune/ui";
import { cn } from "@dune/ui";
import { Button } from "@dune/ui";
import { getInitials } from "@dune/utils";
import { Building02 } from "@untitled-ui/icons-react";
import {
	FileText,
	LogOut,
	Phone,
	Settings,
	User,
	UserSquare,
	Webhook,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { getPrivateFileUrl } from "../../api/queries/files.queries";
import { usePanelSidebarIntegration } from "../../hooks/usePanelSidebarIntegration";
import { AppSidebar } from "./AppSidebar";

interface AppHeaderProps {
	className?: string;
	children?: React.ReactNode;
	onLogout?: () => void;
	userConnected?: UserDetailsPayload & { societyId?: number | null };
}

export const AppHeader = React.forwardRef<HTMLDivElement, AppHeaderProps>(
	({ className, onLogout, userConnected }, ref) => {
		const { role, subrole, societyId } = userConnected ?? {};
		const { state } = useSidebar();
		const isCollapsed = state === "collapsed";
		const navigate = useNavigate();

		const navigateToContacts = () => {
			navigate(PAGES.CONTACTS.index);
		};

		const navigateToBrokerAdmin = () => {
			if (societyId) {
				navigate(PAGES.BROKERS.show.withParams(societyId.toString()));
			}
		};

		return (
			<header
				ref={ref}
				className={cn(
					"flex h-16 justify-between items-center gap-4 bg-sidebar px-6",
					className,
				)}
			>
				<div className="flex items-center">
					<AnimatePresence mode="wait">
						{isCollapsed && (
							<motion.img
								key="header-logo"
								src="/dune_logo.png"
								alt="DUNE Logo"
								className="h-6 mr-4"
								initial={{ opacity: 0, scale: 0.8, x: -20 }}
								animate={{ opacity: 1, scale: 1, x: 0 }}
								exit={{ opacity: 0, scale: 0.8, x: -20 }}
								transition={{
									duration: 0.4,
									ease: [0.4, 0, 0.2, 1],
									delay: 0.1,
								}}
							/>
						)}
					</AnimatePresence>
				</div>
				{/* {<div className="flex-1 min-w-0 max-w-3xl flex justify-center">
					<Input
						type="search"
						placeholder="Recherche..."
						iconOrText={<Search size={24} aria-hidden="true" />}
						iconOrTextPosition="left"
						className="rounded-full bg-background min-w-0"
						disabled
					/>
				</div>} */}
				<div className="flex items-center gap-3">
					{role === "BROKER" && (
						<a
							href="tel:+33189167512"
							className="flex items-center gap-2 rounded-full bg-background-blue px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
						>
							<Phone className="h-4 w-4" />
							+33 (0)1 89 16 75 12
						</a>
					)}
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<button className="outline-none" type="button">
								<UserProfileAvatar userConnected={userConnected} />
							</button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-56">
							<DropdownMenuLabel>Mon compte</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<Link to={PAGES.PROFILE}>
								<DropdownMenuItem>
									<User className="mr-2 h-4 w-4" />
									<span>Profil</span>
								</DropdownMenuItem>
							</Link>
							<DropdownMenuItem disabled>
								<Settings className="mr-2 h-4 w-4" />
								<span>Paramètres</span>
							</DropdownMenuItem>
							{role === "DUNE" && (
								<Link to={PAGES.DOCUMENT_TEMPLATES}>
									<DropdownMenuItem>
										<Building02 className="mr-2 h-4 w-4" />
										<span>Administration</span>
									</DropdownMenuItem>
								</Link>
							)}
							{role === "BROKER" && subrole === "BROKER_ADMIN" && societyId && (
								<DropdownMenuItem onClick={navigateToBrokerAdmin}>
									<Building02 className="mr-2 h-4 w-4" />
									<span>Administration</span>
								</DropdownMenuItem>
							)}
							{role === "DUNE" && (
								<Link to={PAGES.DOCUMENT_TEMPLATES}>
									<DropdownMenuItem>
										<FileText className="mr-2 h-4 w-4" />
										<span>Modèles de documents</span>
									</DropdownMenuItem>
								</Link>
							)}
							{role === "DUNE" && (
								<Link to={PAGES.WEBHOOKS}>
									<DropdownMenuItem>
										<Webhook className="mr-2 h-4 w-4" />
										<span>Webhooks</span>
									</DropdownMenuItem>
								</Link>
							)}
							{role === "DUNE" && (
								<Link to={PAGES.CONTACTS.index}>
									<DropdownMenuItem onClick={navigateToContacts}>
										<UserSquare className="mr-2 h-4 w-4" />
										<span>Contacts</span>
									</DropdownMenuItem>
								</Link>
							)}
							<DropdownMenuSeparator />
							<DropdownMenuItem className="text-red-600" onClick={onLogout}>
								<LogOut className="mr-2 h-4 w-4" />
								<span>Se déconnecter</span>
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
			</header>
		);
	},
);

AppHeader.displayName = "AppHeader";

interface AppLayoutProps {
	children?: React.ReactNode;
	onLogout?: () => void;
	userConnected?: UserDetailsPayload;
	showImpersonationBanner?: boolean;
	onImpersonationLogout?: () => void;
}

export const AppLayout = ({
	children,
	onLogout,
	userConnected,
	showImpersonationBanner,
	onImpersonationLogout,
}: AppLayoutProps) => {
	return (
		<SidebarProvider defaultOpen>
			<SidebarPanelIntegration />
			<AppSidebar userRole={userConnected?.role} />
			<SidebarInset>
				<div className="flex h-full flex-col">
					{showImpersonationBanner && userConnected && (
						<ImpersonationBanner
							userConnected={userConnected}
							onImpersonationLogout={onImpersonationLogout}
						/>
					)}
					<AppHeader onLogout={onLogout} userConnected={userConnected} />

					{children}
					<Outlet />
				</div>
			</SidebarInset>
		</SidebarProvider>
	);
};

const UserProfileAvatar: React.FC<{ userConnected?: UserDetailsPayload }> = ({
	userConnected,
}) => {
	const [profilePictureUrl, setProfilePictureUrl] = React.useState<
		string | null
	>(null);

	React.useEffect(() => {
		const loadProfilePicture = async () => {
			if (userConnected?.profilePicture?.key) {
				try {
					const signedUrl = await getPrivateFileUrl(
						userConnected.profilePicture.key,
					);
					setProfilePictureUrl(signedUrl);
				} catch (error) {
					console.error(
						"Erreur lors du chargement de la photo de profil:",
						error,
					);
				}
			}
		};

		loadProfilePicture();
	}, [userConnected?.profilePicture?.key]);

	return (
		<Avatar>
			{profilePictureUrl ? (
				<AvatarImage
					src={profilePictureUrl}
					alt="Photo de profil"
					className="object-cover"
				/>
			) : (
				<AvatarImage src="/placeholder-avatar.jpg" alt="Photo de profil" />
			)}
			<AvatarFallback className="bg-text-main-blue text-white">
				{getInitials(userConnected?.firstname, userConnected?.lastname)}
			</AvatarFallback>
		</Avatar>
	);
};

export default AppLayout;

type LogOutFromImpersonationButtonProps = {
	onImpersonationLogout?: () => void;
	isInvisible?: boolean;
};

const LogOutFromImpersonationButton = (
	props: LogOutFromImpersonationButtonProps,
) => {
	const { onImpersonationLogout, isInvisible } = props;
	return (
		<Button
			variant="none"
			disabled={isInvisible}
			className={cn("underline", isInvisible && "opacity:0")}
			onClick={onImpersonationLogout}
		>
			Se déconnecter
		</Button>
	);
};

type ImpersonationBannerProps = {
	userConnected: UserDetailsPayload | null;
	onImpersonationLogout?: () => void;
};

const ImpersonationBanner = (props: ImpersonationBannerProps) => {
	const { onImpersonationLogout, userConnected } = props;
	return (
		<div className="bg-orange-400 text-white h-12 flex items-center justify-between relative">
			<LogOutFromImpersonationButton isInvisible />
			Connecté en tant que {userConnected?.firstname} {userConnected?.lastname}
			<LogOutFromImpersonationButton
				onImpersonationLogout={onImpersonationLogout}
			/>
		</div>
	);
};

const SidebarPanelIntegration: React.FC = () => {
	usePanelSidebarIntegration();
	return null;
};
